import { type DesktopCommand, desktopCommandSchema } from '../../shared/desktop';
import type { CairnCapture, CairnDesktop } from '../../shared/desktop-bridge';

/**
 * The page's side of the desktop app (docs/desktop-app.md). In a browser
 * `desktop` is undefined and every helper here does nothing, so the same
 * client runs in both places and only adds the window's chrome, the palette
 * and the About box when it is inside the app.
 */

declare global {
  interface Window {
    cairnDesktop?: CairnDesktop;
    cairnCapture?: CairnCapture;
  }
}

/** The workspace window's bridge, or undefined in a browser and in the capture panel. */
export const desktop: CairnDesktop | undefined =
  typeof window === 'undefined' ? undefined : window.cairnDesktop;

/** The capture panel's bridge, or undefined anywhere else. */
export const captureBridge: CairnCapture | undefined =
  typeof window === 'undefined' ? undefined : window.cairnCapture;

/** Listens for main's commands, dropping anything that is not one of the agreed shapes. */
export function onDesktopCommand(listener: (command: DesktopCommand) => void) {
  if (!desktop) {
    return () => {};
  }

  return desktop.onCommand(payload => {
    const command = desktopCommandSchema.safeParse(payload);

    if (command.success) {
      listener(command.data);
    }
  });
}

/**
 * Asks Today to open Set out. Today may not be on screen yet — the menu
 * navigates there first — so the request waits until Today takes it.
 */
let setOutRequested = false;

export function requestSetOut() {
  setOutRequested = true;
  window.dispatchEvent(new Event('cairn:set-out'));
}

export function takeSetOutRequest() {
  const requested = setOutRequested;

  setOutRequested = false;

  return requested;
}

/** "⌘P" on the Mac, as the menu shows the shortcut. */
export const shortcutLabel = (key: string) => `⌘${key}`;
